// waterfall-geometry.ts — pure step layout for the cost waterfall (Einzahlungen ->
// +Kursgewinn -> -TER -> -Gebühren -> -KESt -> Netto). The component only maps
// these cent ranges onto pixels; all the arithmetic lives here so it's testable
// without rendering (D-007: backtest TER is informational and excluded from the sum).
import type { Waterfall } from '@etf-cockpit/engine';

export type WaterfallBarKind = 'total' | 'positive' | 'negative' | 'informational';

export interface WaterfallBar {
  key: string;
  label: string;
  /** Lower edge of the bar, in cents (always <= toCents). */
  fromCents: number;
  /** Upper edge of the bar, in cents. */
  toCents: number;
  kind: WaterfallBarKind;
  /** The signed delta this step represents (undefined for the two absolute "total" bars). */
  deltaCents?: number;
}

export type WaterfallMode = 'forecast' | 'backtest';

/**
 * Lays out the waterfall as a running-total chart. In backtest mode the TER bar is
 * drawn at its current running-total position (so it visually sits where a forecast
 * TER deduction would) but the running total is NOT decremented by it — TER is already
 * priced into real NAVs (D-007), so it must never affect Gebühren/KESt/Netto positions.
 */
export function computeWaterfallBars(w: Waterfall, mode: WaterfallMode): WaterfallBar[] {
  const bars: WaterfallBar[] = [];
  let running = 0;

  bars.push({
    key: 'contributed',
    label: 'Einzahlungen',
    fromCents: 0,
    toCents: w.contributedCents,
    kind: 'total',
  });
  running = w.contributedCents;

  running = pushDelta(bars, 'grossGain', 'Kursgewinn', running, w.grossGainCents);

  if (mode === 'forecast') {
    running = pushDelta(bars, 'ter', 'TER', running, -w.terCents);
  } else if (w.terInformationalCents !== undefined) {
    // Informational only: drawn, not applied to `running`.
    pushDelta(bars, 'ter', 'TER (bereits im Kurs enthalten)', running, -w.terInformationalCents, true);
  }

  running = pushDelta(bars, 'fees', 'Gebühren', running, -w.feesCents);
  running = pushDelta(bars, 'kest', 'KESt', running, -w.kestCents);

  bars.push({
    key: 'net',
    label: 'Netto',
    fromCents: 0,
    toCents: w.netWealthCents,
    kind: 'total',
  });

  return bars;
}

function pushDelta(
  bars: WaterfallBar[],
  key: string,
  label: string,
  runningBefore: number,
  deltaCents: number,
  informational = false,
): number {
  const runningAfter = runningBefore + deltaCents;
  bars.push({
    key,
    label,
    fromCents: Math.min(runningBefore, runningAfter),
    toCents: Math.max(runningBefore, runningAfter),
    kind: informational ? 'informational' : deltaCents >= 0 ? 'positive' : 'negative',
    deltaCents,
  });
  return runningAfter;
}

/** The full cent range the chart's y-axis must cover (includes 0 and the informational bar). */
export function waterfallExtentCents(bars: WaterfallBar[]): { min: number; max: number } {
  let min = 0;
  let max = 0;
  for (const b of bars) {
    if (b.fromCents < min) min = b.fromCents;
    if (b.toCents > max) max = b.toCents;
  }
  return { min, max };
}
